import {
	Box,
	Checkbox,
	Chip,
	FormControl,
	FormControlLabel,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Modal,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	TextField
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Clinic } from "../../models";
import { HolidayFormData } from "./types";
import {
	Dispatch,
	SetStateAction,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useClinicContext } from "../../contexts";

interface HolidayFormModalProps {
	open: boolean;
	title: string;
	submitLabel: string;
	formData: HolidayFormData;
	setFormData: Dispatch<SetStateAction<HolidayFormData>>;
	onClose: () => void;
	onSubmit: () => void;
}

export const HolidayFormModal = ({
	open,
	title,
	submitLabel,
	formData,
	setFormData,
	onClose,
	onSubmit
}: HolidayFormModalProps) => {
	const { clinics } = useClinicContext();
	const dateInputRef = useRef<HTMLInputElement>(null);
	const [filteredClinics, setFilteredClinics] = useState<Clinic[]>([]);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const descriptionError = !formData.description.trim();
	const clinicsError = formData.clinicIds.length === 0;
	const dateError = !formData.date.trim();
	const isValid = !descriptionError && !clinicsError && !dateError;

	useEffect(() => {
		setSubmitAttempted(false);
	}, [open]);

	const handleSubmit = () => {
		setSubmitAttempted(true);
		if (isValid) onSubmit();
	};

	const clinicStates = useMemo(() => {
		if (!clinics) {
			return [];
		}
		const states = new Set(clinics.flatMap((clinic) => clinic.state));
		return Array.from(states).sort() as string[];
	}, [clinics]);

	const handleChange = (
		field: keyof HolidayFormData,
		value: string | number | boolean | string[] | number[]
	) => {
		setFormData((prev) => {
			const newData = { ...prev, [field]: value };
			if (field === "states") {
				const states = String(value)
					.split(",")
					.map((state) => state.trim())
					.filter(Boolean);
				newData.states = states;
				newData.clinicIds = prev.clinicIds.filter((id) =>
					clinics?.some(
						(clinic) =>
							clinic.id === id &&
							!!clinic.state &&
							states.includes(clinic.state)
					)
				);
			}
			return newData;
		});
	};

	useEffect(() => {
		if (!clinics || clinics.length === 0) {
			setFilteredClinics([]);
			return;
		}

		if (!formData.states || formData.states.length === 0) {
			setFilteredClinics(clinics);
			return;
		}

		setFilteredClinics(
			clinics.filter(
				(clinic) => clinic.state && formData.states.includes(clinic.state)
			)
		);
	}, [clinics, formData.states]);

	const isAllClinicsSelected = useMemo(() => {
		if (filteredClinics.length === 0) return false;
		return filteredClinics.every((clinic) =>
			formData.clinicIds.includes(clinic.id)
		);
	}, [filteredClinics, formData.clinicIds]);

	const handleDateChange = (value: string) => {
		if (!value) {
			handleChange("date", "");
			return;
		}
		handleChange("date", value);
	};

	const handleOpenDatePicker = () => {
		const input = dateInputRef.current;
		if (!input) return;
		input.focus();
		try {
			if (typeof input.showPicker === "function") {
				input.showPicker();
			} else {
				input.click();
			}
		} catch {
			// Keep the focused date input usable if the browser blocks the picker.
			input.click();
		}
	};

	const handleAllClinicsToggle = (checked: boolean) => {
		setFormData((prev) => {
			const clinicIds = checked
				? (clinics || [])
						.filter(
							(clinic) =>
								prev.states.length === 0 ||
								(clinic.state && prev.states.includes(clinic.state))
						)
						.map((clinic) => clinic.id)
				: [];

			return {
				...prev,
				clinicIds
			};
		});
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="holiday-form-modal-title"
		>
			<Box className="modal" sx={{ width: 500 }}>
				<h2 id="holiday-form-modal-title" className="mb-4">
					{title}
				</h2>
				<div className="flex flex-col gap-4">
					<TextField
						label="Description"
						value={formData.description}
						error={submitAttempted && descriptionError}
						helperText={
							submitAttempted && descriptionError
								? "Please enter a description."
								: ""
						}
						onChange={(e) => handleChange("description", e.target.value)}
						fullWidth
						required
					/>

					<div className="flex items-center gap-4">
						<TextField
							label="Date"
							type="date"
							inputRef={dateInputRef}
							value={formData.date}
							error={submitAttempted && dateError}
							helperText={
								submitAttempted && dateError ? "Please enter a date." : ""
							}
							onChange={(e) => handleDateChange(e.target.value)}
							slotProps={{
								inputLabel: { shrink: true },
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="Open date picker"
												onClick={handleOpenDatePicker}
												edge="end"
											>
												<CalendarMonthIcon />
											</IconButton>
										</InputAdornment>
									)
								}
							}}
							sx={{
								"& input::-webkit-calendar-picker-indicator": {
									display: "none"
								}
							}}
							fullWidth
							required
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={formData.recurring}
									onChange={(e) => handleChange("recurring", e.target.checked)}
								/>
							}
							label="Recurring"
						/>
					</div>
					<FormControl fullWidth>
						<InputLabel>States</InputLabel>
						<Select
							multiple
							value={formData.states}
							label="States"
							input={<OutlinedInput label="States" />}
							onChange={(e: SelectChangeEvent<string[]>) => {
								const value = e.target.value;
								handleChange(
									"states",
									typeof value === "string" ? value.split(",") : value
								);
							}}
							renderValue={(selected) => (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
									{selected.map((value) => (
										<Chip key={value} label={value} size="small" />
									))}
								</Box>
							)}
						>
							{clinicStates.map((state) => (
								<MenuItem key={state} value={state}>
									<Checkbox checked={formData.states.includes(state)} />
									<ListItemText primary={state} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<div>
						<FormControlLabel
							control={
								<Checkbox
									checked={isAllClinicsSelected}
									onChange={(event) =>
										handleAllClinicsToggle(event.target.checked)
									}
								/>
							}
							label="All Centres"
						/>
						<FormControl
							fullWidth
							required
							error={submitAttempted && clinicsError}
						>
							<InputLabel>Centres</InputLabel>
							<Select
								multiple
								value={formData.clinicIds}
								inputProps={{ "aria-describedby": "holiday-clinics-error" }}
								label="Clinics"
								input={<OutlinedInput label="Clinics" />}
								onChange={(e: SelectChangeEvent<number[]>) => {
									const value = e.target.value;
									handleChange(
										"clinicIds",
										typeof value === "string"
											? value.split(",").map(Number)
											: value
									);
								}}
								renderValue={(selected) => (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
										{isAllClinicsSelected ? (
											<Chip label="All Clinics" size="small" />
										) : selected.length === 0 ? (
											<span style={{ color: "#999" }}>Select clinics</span>
										) : (
											selected.map((id) => (
												<Chip
													key={id}
													label={clinics?.find((c) => c.id === id)?.name || id}
													size="small"
												/>
											))
										)}
									</Box>
								)}
							>
								{filteredClinics.map((clinic) => (
									<MenuItem key={clinic.id} value={clinic.id}>
										<Checkbox
											checked={formData.clinicIds.includes(clinic.id)}
										/>
										<ListItemText primary={clinic.name} />
									</MenuItem>
								))}
							</Select>
							{submitAttempted && clinicsError && (
								<FormHelperText id="holiday-clinics-error">
									Please select at least one clinic.
								</FormHelperText>
							)}
						</FormControl>
					</div>
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 16,
						marginTop: 24
					}}
				>
					<button className="ch-button ch-button-secondary" onClick={onClose}>
						Cancel
					</button>
					<button
						className="ch-button ch-button-primary"
						onClick={handleSubmit}
					>
						{submitLabel}
					</button>
				</div>
			</Box>
		</Modal>
	);
};
